import { useEffect, useRef, useState } from 'react';
import { LogOut, Menu, X, Shield, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { ADMIN_PAGES } from '../../pages.config.js';

// Friendly labels for the admin nav, overriding the generic "last path
// segment, title-cased" fallback below. "Manage X" instead of "Create X"
// because each of these pages lands on its list/manage view by default
// (see e.g. create-course.jsx's ManageView, which is what an admin
// actually sees first) — "Create" undersells what's there. "Create
// Template" -> "Emails" since that page is really the email-template
// editor, not a generic "create" flow.
const LABEL_OVERRIDES = {
  'admin/create-course': 'Manage Course',
  'admin/create-ferry': 'Manage Ferry',
  'admin/create-transport': 'Manage Transport',
  'admin/create-accommodation': 'Manage Accommodation',
  'admin/create-getaway': 'Manage Getaway',
  'admin/create-template': 'Emails',
};

function labelFromPath(path) {
  if (LABEL_OVERRIDES[path]) return LABEL_OVERRIDES[path];
  const last = path.split('/').pop();
  return last
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function AdminNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [mobileManageOpen, setMobileManageOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Only the 5 catalog-resource "Manage X" pages (née "Create X") live
  // under the "Manage" dropdown. Manage Bookings, "Emails" (née Create
  // Template), and Edit Site are each their own top-level link instead —
  // distinct enough concerns not to be buried in the dropdown. Computed
  // at render time, not module scope — pages.config.js imports
  // Layout.jsx which imports this file, which imports ADMIN_PAGES back
  // from pages.config.js. That circular import means ADMIN_PAGES is
  // still in its temporal dead zone if it's read while this module is
  // first evaluating; reading it here, inside the component, happens
  // after every module has finished loading instead.
  const MANAGE_PAGES = ADMIN_PAGES.filter(
    (p) => p.startsWith('admin/create-') && p !== 'admin/create-template',
  );
  const OUTER_PAGES = ADMIN_PAGES.filter(
    (p) =>
      p === 'admin/create-template' ||
      p === 'admin/manage-bookings' ||
      p === 'admin/settings' ||
      p === 'admin/edit-site'
  );

  const isActive = (path) => location.pathname === `/${path}`;
  const isManageActive = MANAGE_PAGES.some(isActive);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/Login');
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setManageOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-[#0B2421] text-white sticky top-0 z-50 border-b border-white/10">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-6 h-20">
          <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-bold text-lg tracking-wide whitespace-nowrap">
              <span className="text-[#D4AF37]">EXQURA</span>{' '}
              <span className="text-white/60 font-light text-sm">ADMIN</span>
            </span>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-5 flex-1 min-w-0">
            {/* Manage dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setManageOpen((v) => !v)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                  isManageActive ? 'text-[#D4AF37]' : 'text-white/80 hover:text-[#D4AF37]'
                }`}
              >
                Manage
                <ChevronDown className="w-4 h-4" />
              </button>
              {manageOpen && (
                <div className="absolute top-full left-0 mt-3 w-56 bg-[#0B2421] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  {MANAGE_PAGES.map((path, i) => (
                    <Link
                      key={path}
                      to={`/${path}`}
                      onClick={() => setManageOpen(false)}
                      aria-current={isActive(path) ? 'page' : undefined}
                      className={`block px-4 py-3 text-sm transition-colors hover:bg-white/5 ${i > 0 ? 'border-t border-white/5' : ''} ${
                        isActive(path) ? 'text-[#D4AF37]' : 'text-white/80 hover:text-[#D4AF37]'
                      }`}
                    >
                      {labelFromPath(path)}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {OUTER_PAGES.map((path) => (
              <Link
                key={path}
                to={`/${path}`}
                aria-current={isActive(path) ? 'page' : undefined}
                className={`text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                  isActive(path)
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-[#D4AF37]'
                }`}
              >
                {labelFromPath(path)}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-white px-3 py-2 rounded-lg text-sm font-medium hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2 ml-auto shrink-0">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-4 space-y-1">
            {/* Mobile Manage, expandable */}
            <div>
              <button
                onClick={() => setMobileManageOpen((v) => !v)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${
                  isManageActive ? 'text-[#D4AF37]' : 'text-white/80 hover:bg-white/5'
                }`}
              >
                Manage
                <ChevronDown className="w-4 h-4" />
              </button>
              {mobileManageOpen && (
                <div className="ml-4 pl-3 border-l border-white/10 mt-1 space-y-1">
                  {MANAGE_PAGES.map((path) => (
                    <Link
                      key={path}
                      to={`/${path}`}
                      onClick={() => { setMobileOpen(false); setMobileManageOpen(false); }}
                      aria-current={isActive(path) ? 'page' : undefined}
                      className={`block py-2 text-sm transition-colors ${
                        isActive(path) ? 'text-[#D4AF37]' : 'text-white/70 hover:text-[#D4AF37]'
                      }`}
                    >
                      {labelFromPath(path)}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {OUTER_PAGES.map((path) => (
              <Link
                key={path}
                to={`/${path}`}
                aria-current={isActive(path) ? 'page' : undefined}
                onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                  isActive(path)
                    ? 'text-[#D4AF37] bg-white/5'
                    : 'text-white/80 hover:bg-white/5'
                }`}
              >
                {labelFromPath(path)}
              </Link>
            ))}

            <div className="flex items-center gap-2 pt-3">
              <button onClick={handleLogout} className="flex items-center gap-1.5 text-white px-3 py-2 rounded-lg text-sm font-medium bg-white/5">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
